import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Sparkles, Send, Building2, User2, Volume2, VolumeX,
    Languages, MessageSquareText, BarChart3, Bell,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ChartFromJson from "@/components/assistant/ChartFromJson";
import SignalsInbox from "@/components/assistant/SignalsInbox";
import PrefGroup from "@/components/assistant/PrefGroup";
import KimaniOnboarding, { hasCompletedKimaniOnboarding } from "@/components/assistant/KimaniOnboarding";
import MarkdownMessage from "@/components/MarkdownMessage";
import { useAuth } from "@/context/AuthContext";

/**
 * AI Trading Assistant — Kimani workspace, free for ALL authenticated users.
 *
 * Streams tokens via SSE. Portfolio context is auto-prepended so the
 * assistant can reference the user's actual open positions and PnL.
 *
 * Feb 2026 upgrade:
 *   - Language toggle: English · Swahili · Sheng (persists via PATCH /prefs).
 *   - Output-channel toggle: Text · Voice · Graph.
 *       · Voice → browser-native window.speechSynthesis (closed-loop, no external TTS).
 *       · Graph → parses ```chart``` JSON fences and renders inline with recharts.
 *   - Proactive signals inbox — surfaces rows from /api/chat/assistant/signals.
 *
 * Aug 2026 V35.0 directive: Removed ObondoPaywall gate. Kimani is unrestricted
 * on Free Tier. Obondo retains its own paywall in ObondoCoach.jsx + RBACCoPilotGate.
 *
 * Sep 2026: Language toggle expanded from 3 to the full 6-language directive
 * (EN/SW/Sheng/FR/AR/PT) — backend already supported all six.
 */
export const LANG_OPTIONS = [
    { key: "en",    label: "English"   },
    { key: "sw",    label: "Kiswahili" },
    { key: "sheng", label: "Sheng"     },
    { key: "fr",    label: "Français"  },
    { key: "ar",    label: "العربية"  },
    { key: "pt",    label: "Português" },
];
export const CHANNEL_OPTIONS = [
    { key: "text",  label: "Text",  Icon: MessageSquareText },
    { key: "voice", label: "Voice", Icon: Volume2 },
    { key: "graph", label: "Graph", Icon: BarChart3 },
];
export const DEPTH_OPTIONS = [
    { key: "simple",    label: "Simple"    },
    { key: "technical", label: "Technical" },
    { key: "enhanced",  label: "Enhanced"  },
];
export const TTS_LANG_MAP = { sw: "sw-KE", sheng: "sw-KE", fr: "fr-FR", ar: "ar-SA", pt: "pt-PT", en: "en-GB" };
export const TTS_RATE_OPTIONS = [0.75, 1.0, 1.25, 1.5];
const TTS_RATE_STORAGE_KEY = "md_tts_rate";
export function getStoredTtsRate() {
    try {
        const v = parseFloat(localStorage.getItem(TTS_RATE_STORAGE_KEY));
        return TTS_RATE_OPTIONS.includes(v) ? v : 1.0;
    } catch { return 1.0; }
}
export function setStoredTtsRate(rate) {
    try { localStorage.setItem(TTS_RATE_STORAGE_KEY, String(rate)); } catch { /* ignore */ }
}


export default function AiAssistant() {
    return <AiAssistantInner />;
}

function AiAssistantInner() {
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);  // [{role, content}]
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [locked, setLocked] = useState(false);
    const [prefs, setPrefs] = useState({ language: "en", channel: "text", depth: "technical" });
    const [showSignals, setShowSignals] = useState(false);
    const [signalCount, setSignalCount] = useState(0);
    const [showOnboarding, setShowOnboarding] = useState(() => !hasCompletedKimaniOnboarding());
    const [speaking, setSpeaking] = useState(false);
    const [autoPlay, setAutoPlay] = useState(true);
    const [ttsRate, setTtsRate] = useState(getStoredTtsRate);
    const scrollRef = useRef(null);
    const utteranceRef = useRef(null);
    const lastSpokenIdxRef = useRef(-1);

    // Session init + prefs bootstrap ---------------------------------------
    useEffect(() => {
        Promise.all([
            api.post("/chat/assistant/session"),
            api.get("/chat/assistant/preferences"),
        ])
            .then(([sRes, pRes]) => {
                setSessionId(sRes.data.session_id);
                setPrefs(pRes.data.prefs || { language: "en", channel: "text", depth: "technical" });
            })
            .catch((e) => {
                if (e?.response?.status === 403) setLocked(true);
                else toast.error("Could not initialise AI assistant.");
            });
    }, []);

    // Signal count poll (every 60s) ---------------------------------------
    useEffect(() => {
        if (locked) return;
        let alive = true;
        const tick = async () => {
            try {
                const { data } = await api.get("/chat/assistant/signals");
                if (alive) setSignalCount((data.signals || []).length);
            } catch (err) {
                // Poll loop — noisy console during outages is worse than
                // silent degradation. Log once per session at most.
                if (!window.__mdSignalErrLogged) {
                    console.warn("[AiAssistant] signals poll failed (further errors suppressed):", err);
                    window.__mdSignalErrLogged = true;
                }
            }
        };
        tick();
        const id = setInterval(tick, 60_000);
        return () => { alive = false; clearInterval(id); };
    }, [locked]);

    // Autoscroll to latest message ----------------------------------------
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    // Voice output — speak the assistant's last completed message.
    // We only fire when the message is DONE (busy just flipped from true→false),
    // so we don't stutter over streaming deltas. Gated by `autoPlay` so users
    // can turn off automatic narration and only speak messages on demand
    // (see the per-message speaker button in the message list below).
    const speak = useCallback((text) => {
        try {
            if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(stripChartBlocks(text));
            // Language mapping. Sheng isn't a BCP-47 tag; fall back to Swahili.
            u.lang = TTS_LANG_MAP[prefs.language] || "en-GB";
            u.rate = ttsRate;
            u.pitch = 1.0;
            u.onstart = () => setSpeaking(true);
            u.onend = () => setSpeaking(false);
            u.onerror = () => setSpeaking(false);
            utteranceRef.current = u;
            window.speechSynthesis.speak(u);
        } catch { /* browser blocked TTS — silent no-op */ }
    }, [prefs.language, ttsRate]);

    useEffect(() => {
        if (prefs.channel !== "voice" || !autoPlay) return;
        if (busy) return;
        const lastIdx = messages.length - 1;
        if (lastIdx < 0) return;
        if (lastIdx === lastSpokenIdxRef.current) return;
        const last = messages[lastIdx];
        if (!last || last.role !== "assistant" || !last.content) return;
        lastSpokenIdxRef.current = lastIdx;
        speak(last.content);
    }, [messages, busy, prefs.channel, autoPlay, speak]);

    // Silence TTS when the user leaves this page.
    useEffect(() => () => {
        try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    }, []);

    async function updatePrefs(patch) {
        const next = { ...prefs, ...patch };
        setPrefs(next);
        try {
            await api.patch("/chat/assistant/preferences", patch);
        } catch {
            toast.error("Failed to save preferences.");
        }
    }

    async function send() {
        const text = input.trim();
        if (!text || busy) return;
        setBusy(true);
        setInput("");
        setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
        try {
            const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
            // Double-submit CSRF echo — this is a raw fetch() (needed for
            // SSE streaming), so lib/api.js's axios interceptor never runs
            // and the token must be attached explicitly or the backend
            // 403s every message (audit finding, Sept 2026).
            const csrfToken = document.cookie.split(";").map(c => c.trim()).find(c => c.startsWith("md_csrf="))?.split("=")[1];
            const res = await fetch(`${base}/api/chat/assistant/message`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(csrfToken && { "X-CSRF-Token": decodeURIComponent(csrfToken) }),
                },
                credentials: "include",
                body: JSON.stringify({ text, session_id: sessionId, include_portfolio: true }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = buffer.indexOf("\n\n")) !== -1) {
                    const chunk = buffer.slice(0, idx);
                    buffer = buffer.slice(idx + 2);
                    if (chunk.startsWith("event: done")) return;
                    if (chunk.startsWith("event: error")) throw new Error(chunk);
                    if (chunk.startsWith("data: ")) {
                        const delta = chunk.slice(6);
                        setMessages((m) => {
                            const copy = m.slice();
                            copy[copy.length - 1] = {
                                role: "assistant",
                                content: (copy[copy.length - 1].content || "") + delta,
                            };
                            return copy;
                        });
                    }
                }
            }
        } catch (e) {
            toast.error("Assistant stream interrupted. Try again.");
            // Leave a visible error in the bubble instead of a permanent
            // empty "…" — the catch previously left the assistant message
            // stuck with no content and no explanation (audit finding).
            setMessages((m) => {
                const copy = m.slice();
                const lastMsg = copy[copy.length - 1];
                if (lastMsg && lastMsg.role === "assistant" && !lastMsg.content) {
                    copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't get a response — please try again." };
                }
                return copy;
            });
            if (process.env.NODE_ENV !== "production") console.debug("assistant err:", e);
        } finally {
            setBusy(false);
        }
    }

    if (locked) return <LockedPitch />;

    return (
        <div className="max-w-[1400px] mx-auto h-[calc(100vh-4rem)] flex flex-col" data-testid="ai-assistant-page">
            <div className="mb-3">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-primary font-semibold">
                    <Sparkles className="w-3 h-3" /> AI Trading Copilot
                </div>
                <div className="flex items-start justify-between gap-4 mt-1">
                    <div className="min-w-0">
                        <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">
                            <span style={{ color: "var(--melian-primary, #922529)" }}>Kimani</span> — ask anything about your trades.
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Educational only, never trades on your behalf.
                        </p>
                    </div>
                    <button
                        onClick={() => setShowSignals((s) => !s)}
                        data-testid="assistant-signals-toggle"
                        className="shrink-0 relative inline-flex items-center gap-2 min-h-[44px] px-3 rounded-lg border border-border hover:border-primary/50 hover:bg-secondary/40 text-sm font-medium transition-colors"
                    >
                        <Bell className="w-4 h-4" />
                        <span className="hidden sm:inline">Signals</span>
                        {signalCount > 0 && (
                            <span
                                data-testid="assistant-signals-count"
                                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center"
                            >
                                {signalCount > 99 ? "99+" : signalCount}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Preferences bar */}
            <div className="mb-3 flex flex-wrap gap-3 items-center" data-testid="assistant-prefs-bar">
                <PrefGroup
                    testIdPrefix="assistant-lang"
                    icon={Languages}
                    label="Language"
                    value={prefs.language}
                    options={LANG_OPTIONS}
                    onChange={(v) => updatePrefs({ language: v })}
                />
                <PrefGroup
                    testIdPrefix="assistant-channel"
                    label="Output"
                    value={prefs.channel}
                    options={CHANNEL_OPTIONS.map((o) => ({ key: o.key, label: o.label, Icon: o.Icon }))}
                    onChange={(v) => updatePrefs({ channel: v })}
                />
                <PrefGroup
                    testIdPrefix="assistant-depth"
                    label="Depth"
                    value={prefs.depth || "technical"}
                    options={DEPTH_OPTIONS}
                    onChange={(v) => updatePrefs({ depth: v })}
                />
                {prefs.channel === "voice" && (
                    <>
                        <label className="text-xs px-2.5 py-1.5 rounded-md border border-border inline-flex items-center gap-1.5 cursor-pointer select-none" data-testid="assistant-autoplay-toggle">
                            <input
                                type="checkbox"
                                checked={autoPlay}
                                onChange={(e) => setAutoPlay(e.target.checked)}
                                className="accent-primary"
                            />
                            Auto-play replies
                        </label>
                        <label className="text-xs px-2.5 py-1.5 rounded-md border border-border inline-flex items-center gap-1.5" data-testid="assistant-tts-rate-control">
                            Speed
                            <select
                                value={ttsRate}
                                onChange={(e) => { const r = parseFloat(e.target.value); setTtsRate(r); setStoredTtsRate(r); }}
                                className="bg-transparent text-xs font-semibold focus:outline-none cursor-pointer"
                                data-testid="assistant-tts-rate-select"
                            >
                                {TTS_RATE_OPTIONS.map((r) => (
                                    <option key={r} value={r}>{r}x</option>
                                ))}
                            </select>
                        </label>
                        {speaking && (
                            <span
                                className="text-xs px-2.5 py-1.5 rounded-md border border-primary/40 bg-primary/10 text-primary inline-flex items-center gap-1.5 font-semibold"
                                data-testid="assistant-voice-speaking-indicator"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                Speaking…
                            </span>
                        )}
                        <button
                            onClick={() => { try { window.speechSynthesis.cancel(); } catch { /* noop */ } setSpeaking(false); }}
                            className="text-xs px-2.5 py-1.5 rounded-md border border-border hover:border-primary/50 inline-flex items-center gap-1.5"
                            data-testid="assistant-voice-stop"
                        >
                            <VolumeX className="w-3.5 h-3.5" /> Stop speaking
                        </button>
                    </>
                )}
            </div>

            {showSignals && (
                <SignalsInbox
                    onClose={() => setShowSignals(false)}
                    onScanned={(n) => setSignalCount(n)}
                />
            )}

            <div
                ref={scrollRef}
                data-testid="assistant-messages"
                className="flex-1 overflow-y-auto border border-border rounded-t-lg bg-card p-4 space-y-4"
            >
                {messages.length === 0 && (
                    <div className="text-sm text-muted-foreground p-4 text-center">
                        Say hi — try one of the suggestions below or ask anything about your potential investments or trades,
                        Greeks, drawdown mechanics, or the African equities market context.
                        <div className="mt-4 flex flex-wrap justify-center gap-2">
                            {suggestionsFor(prefs.language).map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setInput(s)}
                                    className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary/50 hover:text-primary transition-colors"
                                    data-testid={`assistant-suggestion-${s.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {messages.map((m, i) => (
                    <MessageBubble key={m.id || `${m.role}-${i}`} role={m.role} content={m.content} channel={prefs.channel} onSpeak={speak} />
                ))}
            </div>

            <div className="border-x border-b border-border bg-card rounded-b-lg p-3 flex gap-2">
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                    placeholder="Ask about your portfolio, a concept, or a strategy…"
                    className="flex-1 resize-none h-10 max-h-32 rounded border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
                    data-testid="assistant-input"
                    disabled={busy}
                />
                <button
                    onClick={send}
                    disabled={busy || !input.trim()}
                    data-testid="assistant-send"
                    className="px-4 rounded bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity inline-flex items-center gap-2"
                >
                    <Send className="w-4 h-4" /> {busy ? "Thinking…" : "Send"}
                </button>
            </div>

            {/* Regulatory footnote — mirrors the Landing footer's AI disclaimer.
                Keeps users informed at the exact moment they're most likely
                to over-trust the AI output. Linked to /legal/disclaimer for the
                long-form version. */}
            <p className="text-[11px] text-muted-foreground text-center mt-2 px-2"
               data-testid="assistant-disclaimer-footnote">
                AI outputs are educational commentary — not personalised investment advice.
                {" "}
                <a href="/legal/disclaimer"
                   className="underline underline-offset-2 hover:text-foreground"
                   data-testid="assistant-disclaimer-link">
                    Full disclosure →
                </a>
            </p>

            {showOnboarding && (
                <KimaniOnboarding onComplete={() => setShowOnboarding(false)} />
            )}
        </div>
    );
}

// --------------------------------------------------------------------------
// Language-specific starter prompts. Sheng ≈ Kenyan urban slang.
function suggestionsFor(lang) {
    if (lang === "sw") {
        return [
            "Kagua mkusanyiko wangu wa hisa kwa hatari ya mkusanyiko.",
            "Nieleze delta ya option kwa aya moja.",
            "Kuweka pesa katika CIC MMF kutaboresha Sharpe yangu?",
            "Tofauti kati ya ETF na REIT kwa mwekezaji wa Kenya ni ipi?",
        ];
    }
    if (lang === "sheng") {
        return [
            "Boss, cheki portfolio yangu — kuna concentration risk?",
            "Nieleze option delta kwa ufupi buda.",
            "Nikipark ganji kwa CIC MMF, Sharpe itakuwaje?",
            "ETF vs REIT — ipi ni sawa kwa Nairobi trader?",
        ];
    }
    return [
        "Review my open positions for concentration risk.",
        "Explain option delta in one paragraph.",
        "How would parking idle KES in CIC MMF improve my Sharpe?",
        "What's the difference between an ETF and a REIT for Kenyan investors?",
    ];
}

// --------------------------------------------------------------------------
// Message rendering — handles ```chart JSON``` fences when channel === graph
// --------------------------------------------------------------------------
function MessageBubble({ role, content, channel, onSpeak }) {
    const isUser = role === "user";
    const parts = useMemo(() => splitChartBlocks(content || ""), [content]);
    return (
        <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isUser ? "bg-secondary" : "bg-primary/10 text-primary"}`}>
                {isUser ? <User2 className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
            </div>
            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm leading-relaxed space-y-3 relative group ${
                isUser ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"
            }`}>
                {!isUser && content && (
                    <button
                        onClick={() => onSpeak?.(content)}
                        title="Play this reply aloud"
                        data-testid="assistant-message-speak-btn"
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-card border border-border text-muted-foreground hover:text-primary hover:border-primary/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <Volume2 className="w-3 h-3" />
                    </button>
                )}
                {parts.length === 0 && (
                    <span className="whitespace-pre-wrap">{!isUser ? "…" : ""}</span>
                )}
                {parts.map((p, i) =>
                    p.kind === "chart"
                        ? <ChartFromJson key={`chart-${i}`} spec={p.spec} raw={p.raw} disabled={channel !== "graph"} />
                        : <MarkdownMessage key={`text-${i}`} text={p.text} />
                )}
            </div>
        </div>
    );
}

// Parse ```chart\n{JSON}\n``` fences into structured parts. Non-chart text is
// preserved verbatim so the assistant's prose renders unchanged.
function splitChartBlocks(md) {
    if (!md) return [];
    const parts = [];
    const re = /```chart\s*\n([\s\S]*?)```/g;
    let last = 0;
    let m;
    while ((m = re.exec(md)) !== null) {
        if (m.index > last) parts.push({ kind: "text", text: md.slice(last, m.index) });
        let spec = null;
        try { spec = JSON.parse(m[1].trim()); } catch { /* noop */ }
        parts.push({ kind: "chart", spec, raw: m[1] });
        last = re.lastIndex;
    }
    if (last < md.length) parts.push({ kind: "text", text: md.slice(last) });
    return parts;
}

// Strip fenced chart blocks entirely for TTS narration.
function stripChartBlocks(md) {
    return (md || "").replace(/```chart\s*\n[\s\S]*?```/g, "").trim();
}

function LockedPitch() {
    return (
        <div className="max-w-3xl mx-auto p-6 md:p-10" data-testid="ai-assistant-locked">
            <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
                <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Sparkles className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="overline text-primary mb-1">Institutional · included</div>
                        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight leading-tight">
                            Meet <span style={{ color: "var(--melian-primary, #922529)" }}>Obondo</span> — the AI Copilot that reads your book.
                        </h1>
                        <p className="text-sm md:text-base text-muted-foreground mt-2">
                            Obondo is institutional-tier because it needs live access to your open positions,
                            drawdown state, and Greek exposure. Individual-tier users get <b className="text-foreground">Kimani</b>,
                            our Sheng-speaking learning buddy who explains the concepts.
                        </p>
                    </div>
                </div>

                {/* Three-bullet "what it does" reminder — matches the landing promise */}
                <div className="grid sm:grid-cols-3 gap-3 mb-6">
                    {[
                        { title: "Pre-trade review",       body: "Concentration + drawdown check before you BUY." },
                        { title: "Proactive signals",      body: "Buy / Sell / Trim recs fired every 5 minutes." },
                        { title: "Trilingual · multimodal",body: "English · Kiswahili · Sheng · text / voice / chart." },
                    ].map((b) => (
                        <div key={b.title} className="rounded border border-border bg-background/50 p-3">
                            <div className="text-xs font-extrabold mb-1">{b.title}</div>
                            <div className="text-[11px] text-muted-foreground leading-snug">{b.body}</div>
                        </div>
                    ))}
                </div>

                {/* Dual CTA — primary institutional path + secondary Kimani soft-path */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <a
                        href="/for-brokers"
                        data-testid="assistant-upgrade-cta"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity"
                    >
                        <Building2 className="w-4 h-4" /> View institutional plan
                    </a>
                    <a
                        href="/faq"
                        data-testid="assistant-kimani-fallback"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded border border-border text-sm font-semibold hover:bg-secondary/50 transition-colors"
                    >
                        <MessageSquareText className="w-4 h-4" /> Ask Kimani in the FAQ
                    </a>
                </div>

                <p className="text-[11px] text-muted-foreground text-center mt-4">
                    Institutional plans: MoE (KES 350,000/yr) · CMA (KES 500,000/yr) · up to 100 seats · pay by bank transfer.
                </p>
            </div>
        </div>
    );
}
